import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { OptionsService } from '../../../shared/services/options.service';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import {
  OPTION_CATEGORY_LABEL,
  OPTION_CATEGORY_ORDER,
  OptionCategory,
  VehicleOption,
} from '../../../shared/types/option.types';
import { Vehicle } from '../../../shared/types/vehicle.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

interface OptionGroup {
  category: OptionCategory;
  label: string;
  options: VehicleOption[];
}

/**
 * Section "Équipements" sur la fiche véhicule owner — liste les options
 * actuellement rattachées (chips groupés par catégorie), permet d'en
 * retirer ou d'en ajouter via un picker modal alimenté par le référentiel
 * `GET /rental/api/options`.
 *
 * Mutations :
 * - Ajout : POST `/api/vehicles/{id}/me/options` avec `{ optionIds: [] }`.
 * - Retrait : DELETE `/api/vehicles/{id}/me/options/{optionId}`.
 *
 * Émet {@link optionsChanged} avec le véhicule à jour pour que le parent
 * resync son signal sans refetch HTTP.
 */
@Component({
  selector: 'app-vehicle-options',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalComponent, TPipe],
  templateUrl: './vehicle-options.component.html',
    styleUrls: ['./vehicle-options.component.scss'],
})
export class VehicleOptionsComponent implements OnInit {
  @Input({ required: true }) vehicleId!: number;
  /** Signal input : doit l'être pour que les `computed` ci-dessous se
   *  recalculent quand le parent re-binde la liste après ajout/retrait. */
  readonly currentOptions = input<VehicleOption[] | null | undefined>([]);

  @Output() readonly optionsChanged = new EventEmitter<Vehicle>();

  private readonly optionsService = inject(OptionsService);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly catLabel = OPTION_CATEGORY_LABEL;

  readonly catalog = signal<VehicleOption[]>([]);
  readonly catalogLoading = signal(false);
  readonly catalogError = signal<string | null>(null);

  /** Spinner par option pour la suppression d'un chip. */
  readonly busyOptionId = signal<number | null>(null);
  readonly removeError = signal<string | null>(null);

  // Picker modal
  readonly pickerOpen = signal(false);
  readonly picking = signal(false);
  readonly pickerError = signal<string | null>(null);
  readonly searchCtrl = new FormControl('', { nonNullable: true });
  readonly searchTerm = signal('');
  readonly selectedIds = signal<Set<number>>(new Set());

  /** Options groupées par catégorie pour l'affichage chips. */
  readonly groupedCurrent = computed<OptionGroup[]>(() => {
    const current = this.currentOptions() ?? [];
    return this.groupByCategory(current);
  });

  /** Options du catalogue qui ne sont pas encore rattachées, filtrées par
   *  le terme de recherche, puis groupées par catégorie. */
  readonly groupedAvailable = computed<OptionGroup[]>(() => {
    const currentIds = new Set((this.currentOptions() ?? []).map((o) => o.id));
    const term = this.searchTerm().trim().toLowerCase();
    const available = this.catalog().filter((o) => {
      if (currentIds.has(o.id)) return false;
      if (!term) return true;
      return (
        o.nameFr.toLowerCase().includes(term) ||
        (o.nameEn ?? '').toLowerCase().includes(term) ||
        o.slug.toLowerCase().includes(term)
      );
    });
    return this.groupByCategory(available);
  });

  readonly selectedCount = computed(() => this.selectedIds().size);

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.searchTerm.set(v ?? ''));
  }

  // ── Picker ────────────────────────────────────────────────────────────
  openPicker(): void {
    this.pickerError.set(null);
    this.selectedIds.set(new Set());
    this.searchCtrl.setValue('');
    this.pickerOpen.set(true);
    if (!this.catalog().length) {
      this.loadCatalog();
    }
  }

  closePicker(): void {
    if (this.picking()) return;
    this.pickerOpen.set(false);
  }

  toggleSelect(opt: VehicleOption): void {
    const next = new Set(this.selectedIds());
    if (next.has(opt.id)) next.delete(opt.id);
    else next.add(opt.id);
    this.selectedIds.set(next);
  }

  isSelected(opt: VehicleOption): boolean {
    return this.selectedIds().has(opt.id);
  }

  loadCatalog(): void {
    this.catalogLoading.set(true);
    this.catalogError.set(null);
    this.optionsService
      .list({ size: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.catalog.set(res?.data ?? []);
          this.catalogLoading.set(false);
        },
        error: (err: unknown) => {
          this.catalog.set([]);
          this.catalogLoading.set(false);
          this.catalogError.set(
            toErrorMessage(err, 'errors.optionsCatalogLoadFailed'),
          );
        },
      });
  }

  confirmPick(): void {
    const ids = Array.from(this.selectedIds());
    if (!ids.length || this.picking()) return;
    this.picking.set(true);
    this.pickerError.set(null);
    this.vehiclesService
      .addOptions(this.vehicleId, ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.picking.set(false);
          this.pickerOpen.set(false);
          this.selectedIds.set(new Set());
          this.optionsChanged.emit(v);
        },
        error: (err: unknown) => {
          this.picking.set(false);
          this.pickerError.set(
            toErrorMessage(err, "Impossible d'ajouter les équipements."),
          );
        },
      });
  }

  // ── Remove chip ───────────────────────────────────────────────────────
  remove(opt: VehicleOption): void {
    if (this.busyOptionId() === opt.id) return;
    this.busyOptionId.set(opt.id);
    this.removeError.set(null);
    this.vehiclesService
      .removeOption(this.vehicleId, opt.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.busyOptionId.set(null);
          this.optionsChanged.emit(v);
        },
        error: (err: unknown) => {
          this.busyOptionId.set(null);
          this.removeError.set(
            toErrorMessage(err, "Impossible de retirer l'équipement."),
          );
        },
      });
  }

  private groupByCategory(list: VehicleOption[]): OptionGroup[] {
    const buckets = new Map<OptionCategory, VehicleOption[]>();
    for (const opt of list) {
      const cat = (opt.category ?? 'AUTRE') as OptionCategory;
      const bucket = buckets.get(cat);
      if (bucket) bucket.push(opt);
      else buckets.set(cat, [opt]);
    }
    const groups: OptionGroup[] = [];
    for (const cat of OPTION_CATEGORY_ORDER) {
      const opts = buckets.get(cat);
      if (opts?.length) {
        opts.sort((a, b) => a.nameFr.localeCompare(b.nameFr, 'fr'));
        groups.push({ category: cat, label: OPTION_CATEGORY_LABEL[cat], options: opts });
      }
    }
    return groups;
  }
}
